import { apiService } from './apiService';
import type { 
  InitiateUploadRequest, 
  ChunkUploadRequest, 
  CompleteUploadRequest, 
  AbortUploadRequest,
  UploadPart,
  UploadProgress,
  ChunkProgress,
  FileItem,
  ApiError 
} from '../types/api';

export interface ChunkedUploadOptions {
  chunkSize?: number;
  maxRetries?: number;
  retryDelay?: number;
  folderId?: string | null;
  onProgress?: (progress: UploadProgress) => void;
  onChunkProgress?: (chunkProgress: ChunkProgress) => void;
  onError?: (error: ApiError) => void;
  abortSignal?: AbortSignal;
}

export interface ChunkedUploadState {
  uploadId: string;
  s3Key: string;
  file: File;
  appId: string;
  folderId?: string | null;
  chunks: Blob[];
  uploadedParts: UploadPart[];
  currentChunk: number;
  totalChunks: number;
  totalUploaded: number;
  startTime: number;
}

export class ChunkedUploadService {
  private static readonly DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly DEFAULT_MAX_RETRIES = 3;
  private static readonly DEFAULT_RETRY_DELAY = 1000; // 1 second

  static async uploadFile(
    file: File, 
    appId: string, 
    options: ChunkedUploadOptions = {}
  ): Promise<FileItem> {
    const {
      chunkSize = this.DEFAULT_CHUNK_SIZE,
      maxRetries = this.DEFAULT_MAX_RETRIES,
      retryDelay = this.DEFAULT_RETRY_DELAY,
      onProgress,
      onChunkProgress,
      onError,
      abortSignal
    } = options;

    try {
      // Check if upload was aborted
      if (abortSignal?.aborted) {
        throw new Error('Upload aborted');
      }

      // Initiate upload
      const initiateRequest: InitiateUploadRequest = {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      };

      const initiateResponse = await apiService.initiateUpload(appId, initiateRequest);

      // Create upload state
      const chunks = this.createChunks(file, chunkSize);
      const uploadState: ChunkedUploadState = {
        uploadId: initiateResponse.uploadId,
        s3Key: initiateResponse.s3Key,
        file,
        appId,
        folderId: options.folderId,
        chunks,
        uploadedParts: [],
        currentChunk: 0,
        totalChunks: chunks.length,
        totalUploaded: 0,
        startTime: Date.now()
      };

      // Upload chunks
      for (let i = 0; i < chunks.length; i++) {
        if (abortSignal?.aborted) {
          await this.abortUpload(uploadState);
          throw new Error('Upload aborted');
        }

        const chunk = chunks[i];
        const partNumber = i + 1;
        
        let retries = 0;
        let etag: string | null = null;

        while (retries <= maxRetries && !etag) {
          try {
            etag = await this.uploadChunk(uploadState, chunk, partNumber);
          } catch (error) {
            retries++;
            if (retries > maxRetries) {
              if (onError) {
                onError(error as ApiError);
              }
              await this.abortUpload(uploadState);
              throw error;
            }
            
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, retryDelay * retries));
          }
        }

        if (etag) {
          uploadState.uploadedParts.push({
            PartNumber: partNumber,
            ETag: etag
          });
          
          uploadState.currentChunk = i + 1;
          uploadState.totalUploaded += chunk.size;

          // Report chunk progress
          if (onChunkProgress) {
            onChunkProgress({
              chunkNumber: partNumber,
              loaded: chunk.size,
              total: chunk.size,
              percentage: 100
            });
          }

          // Report overall progress
          if (onProgress) {
            const elapsed = Date.now() - uploadState.startTime;
            const speed = uploadState.totalUploaded / (elapsed / 1000);
            const remaining = (file.size - uploadState.totalUploaded) / speed;
            
            onProgress({
              loaded: uploadState.totalUploaded,
              total: file.size,
              percentage: Math.round((uploadState.totalUploaded / file.size) * 100),
              speed,
              timeRemaining: remaining
            });
          }
        }
      }

      // Complete upload
      const completeRequest: CompleteUploadRequest = {
        uploadId: uploadState.uploadId,
        s3Key: uploadState.s3Key,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        folderId: uploadState.folderId,
        parts: uploadState.uploadedParts
      };

      const uploadedFile = await apiService.completeUpload(appId, completeRequest);
      
      // Final progress update
      if (onProgress) {
        onProgress({
          loaded: file.size,
          total: file.size,
          percentage: 100,
          speed: file.size / ((Date.now() - uploadState.startTime) / 1000)
        });
      }

      return uploadedFile;

    } catch (error) {
      if (onError) {
        onError(error as ApiError);
      }
      throw error;
    }
  }

  private static createChunks(file: File, chunkSize: number): Blob[] {
    const chunks: Blob[] = [];
    let offset = 0;

    while (offset < file.size) {
      const chunk = file.slice(offset, offset + chunkSize);
      chunks.push(chunk);
      offset += chunkSize;
    }

    return chunks;
  }

  private static async uploadChunk(
    uploadState: ChunkedUploadState,
    chunk: Blob,
    partNumber: number
  ): Promise<string> {
    const chunkRequest: ChunkUploadRequest = {
      uploadId: uploadState.uploadId,
      s3Key: uploadState.s3Key,
      partNumber
    };

    const chunkResponse = await apiService.getChunkUploadUrl(uploadState.appId, chunkRequest);
    const etag = await apiService.uploadChunk(chunkResponse.url, chunk);
    
    return etag;
  }

  private static async abortUpload(uploadState: ChunkedUploadState): Promise<void> {
    const abortRequest: AbortUploadRequest = {
      uploadId: uploadState.uploadId,
      s3Key: uploadState.s3Key
    };

    try {
      await apiService.abortUpload(uploadState.appId, abortRequest);
    } catch (error) {
      console.error('Failed to abort upload:', error);
    }
  }

  // Utility methods
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  static formatSpeed(bytesPerSecond: number): string {
    return this.formatFileSize(bytesPerSecond) + '/s';
  }

  static formatTimeRemaining(seconds: number): string {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  }
} 